import { useState, type FormEvent } from 'react';
import { platformBaseUrl } from '../api/client';
import { describeSignInFailure } from './useViewer';

interface SignInScreenProps {
  onSignIn: (email: string, password: string) => Promise<void>;
}

/**
 * Whether a cookie the API sets can reach this page.
 *
 * The API scopes its session cookie to the parent domain, so two subdomains of
 * one domain share it and two unrelated domains cannot. The comparison is the
 * last two labels, which is the domain these deployments actually use; a public
 * suffix list would be more correct and is not worth its weight for a hint.
 */
function sharesParentDomain(left: string, right: string): boolean {
  if (left === right) return true;
  const parent = (host: string): string => host.split('.').slice(-2).join('.');
  return parent(left) === parent(right);
}

export function SignInScreen({ onSignIn }: SignInScreenProps): React.JSX.Element {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [problem, setProblem] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);

  // The session cookie is scoped to a shared parent domain by the API's
  // SESSION_COOKIE_DOMAIN, so a dashboard on a sibling subdomain — the intended
  // production shape, admin and api under one domain — does keep the session.
  // Warning there would be wrong, and it used to be: the check was "the two host
  // names differ", which is exactly how a correct deployment looks. What cannot
  // work is two unrelated domains, where the API's cookie is for its own domain
  // and the browser never sends it: signing in appears to work, then the next
  // check drops it.
  const api = new URL(platformBaseUrl, window.location.origin);
  const hostMismatch = !sharesParentDomain(window.location.hostname, api.hostname);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setProblem(undefined);
    setBusy(true);
    try {
      await onSignIn(email, password);
    } catch (error) {
      setProblem(describeSignInFailure(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="screen">
      <div className="auth">
        <header className="auth__head">
          <span className="brand__mark-box" aria-hidden="true">
            SFC
          </span>
          <p className="auth__org">Society for Code</p>
          <h1>Dashboard</h1>
          <p className="lede">Sign in to manage the site.</p>
        </header>

        <form className="auth__card" onSubmit={submit} noValidate>
          {hostMismatch ? (
            <p className="notice notice--once" data-testid="host-hint">
              This page is on <code>{window.location.host}</code> and the API is on{' '}
              <code>{api.host}</code>. Those are different domains, so the session cookie the API sets
              will not come back here: the dashboard has to be served from the same domain as the API.
            </p>
          ) : null}

          <div className="field">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </div>

          {problem ? (
            <p className="notice notice--problem" role="alert" data-testid="sign-in-problem">
              {problem}
            </p>
          ) : null}

          <button className="button button--primary button--block" type="submit" disabled={busy}>
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <p className="auth__foot">
          Accounts are created by an operator. There is no sign-up, and no default password.
        </p>
      </div>
    </main>
  );
}
